import React, { Component } from 'react'
import {
  MDBBreadcrumb,
  MDBBreadcrumbItem,
  MDBBtn,
  MDBCard,
  MDBCardBody,
  MDBContainer,
  MDBRow,
  MDBCol,
} from 'mdb-react-ui-kit'
import DashboardLayout from '../../components/Admin/DashboardLayout'
import WebsiteBlogPostService from '../../shared/services/WebsiteBlogPost.service'
import TextEditor from '../../components/shared/TextEditor/TextEditor.component'
import { withRouter } from 'react-router-dom'
import { toast } from 'react-toastify'

const slugify = text =>
  text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim()

class BlogPostEdit extends Component {
  state = {
    saving: false,
    loading: false,
    isEdit: false,
    title: '',
    slug: '',
    summary: '',
    content: '',
    featured_image: '',
    category: '',
    tags: '',
    status: 'draft',
    published_at: '',
  }

  componentDidMount() {
    const {
      match: { params },
    } = this.props
    if (params && params.id) {
      this.setState({ isEdit: true })
      this.fetchData(params.id)
    }
  }

  fetchData = async id => {
    this.setState({ loading: true })
    try {
      const data = await WebsiteBlogPostService.get(id)
      if (data) {
        let tagsStr = ''
        if (data.tags) {
          try {
            const parsed = typeof data.tags === 'string' ? JSON.parse(data.tags) : data.tags
            tagsStr = Array.isArray(parsed) ? parsed.join(', ') : ''
          } catch (e) {
            tagsStr = ''
          }
        }

        this.setState({
          title: data.title || '',
          slug: data.slug || '',
          summary: data.summary || '',
          content: data.content || '',
          featured_image: data.featured_image || '',
          category: data.category || '',
          tags: tagsStr,
          status: data.status || 'draft',
          published_at: data.published_at ? new Date(data.published_at).toISOString().slice(0, 16) : '',
        })
      }
    } catch (error) {
      toast.error('Failed to load blog post.', {
        position: toast.POSITION.TOP_RIGHT,
      })
    } finally {
      this.setState({ loading: false })
    }
  }

  onTitleChange = e => {
    const title = e.target.value
    const updates = { title }
    if (!this.state.isEdit) {
      updates.slug = slugify(title)
    }
    this.setState(updates)
  }

  save = async () => {
    const { saving, isEdit, title, slug, summary, content, featured_image, category, tags, status, published_at } =
      this.state
    if (saving) return

    if (!title.trim()) {
      toast.error('Title is required.', { position: toast.POSITION.TOP_RIGHT })
      return
    }
    if (!slug.trim()) {
      toast.error('Slug is required.', { position: toast.POSITION.TOP_RIGHT })
      return
    }

    this.setState({ saving: true })

    const tagsArray = tags
      ? tags
          .split(',')
          .map(t => t.trim())
          .filter(Boolean)
      : []

    const payload = {
      title: title.trim(),
      slug: slug.trim(),
      summary: summary.trim(),
      content,
      featured_image: featured_image.trim(),
      category: category.trim(),
      tags: JSON.stringify(tagsArray),
      status,
      published_at: published_at || null,
    }

    try {
      if (isEdit) {
        const id = this.props.match.params.id
        await WebsiteBlogPostService.update(id, payload)
        toast.success('Blog post updated successfully.', {
          position: toast.POSITION.TOP_RIGHT,
        })
      } else {
        await WebsiteBlogPostService.store(payload)
        toast.success('Blog post created successfully.', {
          position: toast.POSITION.TOP_RIGHT,
        })
        this.props.history.push('/admin-blog-posts')
      }
    } catch (error) {
      toast.error('Failed to save blog post.', {
        position: toast.POSITION.TOP_RIGHT,
      })
    } finally {
      this.setState({ saving: false })
    }
  }

  render() {
    const {
      isEdit,
      loading,
      saving,
      title,
      slug,
      summary,
      content,
      featured_image,
      category,
      tags,
      status,
      published_at,
    } = this.state

    const goBack = () => this.props.history.goBack()

    if (loading) {
      return (
        <DashboardLayout>
          <main className='mainSection'>
            <MDBContainer fluid className='mt-5'>
              <h4>Loading...</h4>
            </MDBContainer>
          </main>
        </DashboardLayout>
      )
    }

    return (
      <DashboardLayout>
        <main className='mainSection'>
          <MDBContainer fluid className='mt-5'>
            <h2>
              {isEdit ? 'Edit Blog Post' : 'Create Blog Post'}
              <div className='float-right'>
                <MDBBtn type='button' color='danger' rounded size='sm' onClick={goBack}>
                  Cancel
                </MDBBtn>
              </div>
            </h2>

            {isEdit && (
              <MDBBreadcrumb>
                <MDBBreadcrumbItem>
                  <a href='/admin-blog-posts'>Blog Posts</a>
                </MDBBreadcrumbItem>
                <MDBBreadcrumbItem active>Editing &quot;{title}&quot;</MDBBreadcrumbItem>
              </MDBBreadcrumb>
            )}

            <MDBCard>
              <MDBCardBody>
                <MDBRow>
                  <MDBCol md='8'>
                    <label className='font-weight-bold'>Title *</label>
                    <input
                      type='text'
                      className='form-control mb-3'
                      value={title}
                      onChange={this.onTitleChange}
                      placeholder='Blog post title'
                    />
                  </MDBCol>
                  <MDBCol md='4'>
                    <label className='font-weight-bold'>Slug *</label>
                    <input
                      type='text'
                      className='form-control mb-3'
                      value={slug}
                      onChange={e => this.setState({ slug: e.target.value })}
                      placeholder='url-friendly-slug'
                    />
                  </MDBCol>
                </MDBRow>

                <MDBRow>
                  <MDBCol md='4'>
                    <label className='font-weight-bold'>Category</label>
                    <input
                      type='text'
                      className='form-control mb-3'
                      value={category}
                      onChange={e => this.setState({ category: e.target.value })}
                      placeholder='e.g. health, medicare'
                    />
                  </MDBCol>
                  <MDBCol md='3'>
                    <label className='font-weight-bold'>Status</label>
                    <select
                      className='form-control mb-3'
                      value={status}
                      onChange={e => this.setState({ status: e.target.value })}
                    >
                      <option value='draft'>Draft</option>
                      <option value='published'>Published</option>
                      <option value='archived'>Archived</option>
                    </select>
                  </MDBCol>
                  <MDBCol md='5'>
                    <label className='font-weight-bold'>Published At</label>
                    <input
                      type='datetime-local'
                      className='form-control mb-3'
                      value={published_at}
                      onChange={e => this.setState({ published_at: e.target.value })}
                    />
                  </MDBCol>
                </MDBRow>

                <MDBRow>
                  <MDBCol md='6'>
                    <label className='font-weight-bold'>Tags (comma-separated)</label>
                    <input
                      type='text'
                      className='form-control mb-3'
                      value={tags}
                      onChange={e => this.setState({ tags: e.target.value })}
                      placeholder='health, insurance, tips'
                    />
                  </MDBCol>
                  <MDBCol md='6'>
                    <label className='font-weight-bold'>Featured Image URL</label>
                    <input
                      type='text'
                      className='form-control mb-3'
                      value={featured_image}
                      onChange={e => this.setState({ featured_image: e.target.value })}
                      placeholder='https://...'
                    />
                  </MDBCol>
                </MDBRow>

                <label className='font-weight-bold'>Summary</label>
                <textarea
                  className='form-control mb-3'
                  rows='3'
                  value={summary}
                  onChange={e => this.setState({ summary: e.target.value })}
                  placeholder='Short excerpt for listing pages'
                />

                <hr />

                <label className='font-weight-bold'>Content</label>
                <TextEditor id='blog-post-editor' value={content} onChange={html => this.setState({ content: html })} />

                <div className='mt-3'>
                  <MDBBtn type='button' color='unique' onClick={this.save}>
                    {saving ? 'Saving...' : 'Save'}
                  </MDBBtn>
                </div>
              </MDBCardBody>
            </MDBCard>
          </MDBContainer>
        </main>
      </DashboardLayout>
    )
  }
}

export default withRouter(BlogPostEdit)
